import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Gem, Layers, Mountain } from "lucide-react";
import { Card, CHART_COLORS, tooltipStyle } from "./shared";

const EPOCH_COLOR = {
    Amazonian: "bg-ares-amber/25 border-ares-amber/50",
    Hesperian: "bg-ares-orange/25 border-ares-orange/50",
    Noachian: "bg-ares-cyan/20 border-ares-cyan/50",
    "Pre-Noachian": "bg-ares-emerald/20 border-ares-emerald/50",
};

function StratColumn({ layers }) {
    const max = Math.max(...layers.map((l) => Math.log10(l.thickness_m + 1)));
    return (
        <div className="space-y-1" data-testid="geology-strat-column">
            {layers.map((l, i) => (
                <div key={i} className={`rounded-md border px-3 flex items-center justify-between ${EPOCH_COLOR[l.epoch] || "bg-ares-panel border-ares-border"}`}
                    style={{ minHeight: 34 + (Math.log10(l.thickness_m + 1) / max) * 40 }} data-testid={`strat-layer-${i}`}>
                    <div>
                        <p className="text-sm font-medium">{l.unit}</p>
                        <p className="font-mono text-[10px] uppercase tracking-wider text-ares-muted">{l.epoch}</p>
                    </div>
                    <span className="font-mono text-xs text-ares-bright">{l.thickness_m.toLocaleString()} m</span>
                </div>
            ))}
            <p className="font-mono text-[10px] text-ares-muted pt-1">Top = youngest · thickness on log scale</p>
        </div>
    );
}

export default function GeologyPanel({ data }) {
    const g = data.geology;
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card title="Mineral assemblage (modal %)" icon={Gem} testid="geology-minerals-card">
                <div style={{ height: 230 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={g.minerals} layout="vertical" margin={{ left: 20 }}>
                            <XAxis type="number" stroke="#64748b" fontSize={11} unit="%" />
                            <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} width={110} />
                            <Tooltip {...tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                            <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
                                {g.minerals.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>
            <Card title="Stratigraphic column" icon={Layers} testid="geology-strat-card">
                <StratColumn layers={g.stratigraphy} />
            </Card>
            <Card title="Rock record" icon={Mountain} testid="geology-rocks-card" className="lg:col-span-2">
                <p className="text-sm text-ares-muted mb-3">Formation era: <span className="text-ares-bright" data-testid="geology-era">{g.era}</span></p>
                <div className="flex flex-wrap gap-2">
                    {g.rock_types.map((r) => (
                        <span key={r} className="rounded-full border border-ares-border bg-ares-panel px-3 py-1 text-xs">{r}</span>
                    ))}
                </div>
            </Card>
        </div>
    );
}
